import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, Trash2, ChevronDown, ChevronUp, Pencil, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { authFetch, API } from '../../utils/api';


const CONDITION_COLORS = {
  'New': 'text-green-400 bg-green-400/10',
  'Good': 'text-primary bg-primary/10',
  'Fair': 'text-yellow-400 bg-yellow-400/10',
  'Thrashed': 'text-red-400 bg-red-400/10',
};

const TYPE_ICONS = {
  'Boardies / Swimmers': '🩳',
  'Sting Suit': '🦟',
  'Rash Guard': '👕',
  'Drysuit': '🧊',
};

function getThicknessIcon(type) {
  if (TYPE_ICONS[type]) return TYPE_ICONS[type];
  // Wetsuit — show a wave-like indicator
  return '🤿';
}

export default function SuitsSection() {
  const [suits, setSuits] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Form state
  const [form, setForm] = useState({ suit_type: '', brand: '', condition: 'Good', color: '', notes: '' });

  const load = useCallback(async () => {
    try {
      const [suitsRes, optsRes] = await Promise.all([
        authFetch(`${API}/api/suits`),
        authFetch(`${API}/api/suits/options`),
      ]);
      if (suitsRes.ok) setSuits((await suitsRes.json()).suits || []);
      if (optsRes.ok) {
        const data = await optsRes.json();
        setTypeOptions(data.types || []);
        setBrandOptions(data.brands || []);
      }
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm({ suit_type: '', brand: '', condition: 'Good', color: '', notes: '' });

  const handleSave = async () => {
    if (!form.suit_type) return;
    try {
      if (editingId) {
        await authFetch(`${API}/api/suits/${editingId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },           body: JSON.stringify(form),
        });
      } else {
        await authFetch(`${API}/api/suits`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },           body: JSON.stringify(form),
        });
      }
      load();
      setShowAdd(false);
      setEditingId(null);
      resetForm();
    } catch {}
  };

  const handleDelete = async (suitId) => {
    try {
      await authFetch(`${API}/api/suits/${suitId}`, { method: 'DELETE',  });
      load();
    } catch {}
  };

  const startEdit = (suit) => {
    setForm({ suit_type: suit.suit_type, brand: suit.brand || '', condition: suit.condition || 'Good', color: suit.color || '', notes: suit.notes || '' });
    setEditingId(suit.suit_id);
    setShowAdd(true);
    setExpanded(true);
  };

  if (loading) return null;

  return (
    <div className="bg-bg-subtle/60 border border-white/[0.06] rounded-2xl overflow-hidden" data-testid="suits-section">
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors"
        data-testid="suits-toggle"
      >
        <div className="flex items-center gap-2.5">
          <span className="text-base">🤿</span>
          <div className="text-left">
            <p className="text-xs font-semibold">Suits</p>
            <p className="text-[9px] text-muted-foreground/50">{suits.length} suit{suits.length !== 1 ? 's' : ''} in your closet</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!expanded && suits.length > 0 && (
            <div className="flex -space-x-1">
              {suits.slice(0, 4).map((s, i) => (
                <span key={i} className="text-[10px] bg-white/5 rounded-full w-5 h-5 flex items-center justify-center border border-white/10">
                  {getThicknessIcon(s.suit_type)}
                </span>
              ))}
              {suits.length > 4 && (
                <span className="text-[8px] bg-white/5 rounded-full w-5 h-5 flex items-center justify-center border border-white/10 text-muted-foreground">
                  +{suits.length - 4}
                </span>
              )}
            </div>
          )}
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground/40" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/40" />}
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 border-t border-white/[0.04]">
              {/* Add button */}
              <div className="flex justify-end mb-2">
                <button
                  data-testid="suits-add-btn"
                  onClick={() => { setShowAdd(!showAdd); if (showAdd) { setEditingId(null); resetForm(); } }}
                  className="text-[10px] font-medium text-primary hover:text-primary/80 flex items-center gap-1 transition-colors"
                >
                  {showAdd ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                  {showAdd ? 'Cancel' : 'Add suit'}
                </button>
              </div>

              {/* Add / Edit form */}
              <AnimatePresence>
                {showAdd && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden mb-3"
                  >
                    <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3 space-y-2.5" data-testid="suit-form">
                      <p className="text-[9px] uppercase tracking-widest text-muted-foreground/50 font-bold">
                        {editingId ? 'Edit Suit' : 'New Suit'}
                      </p>

                      {/* Type */}
                      <div>
                        <label className="text-[9px] text-muted-foreground/60 mb-0.5 block">Type / Thickness</label>
                        <select
                          data-testid="suit-type-select"
                          value={form.suit_type}
                          onChange={e => setForm(p => ({ ...p, suit_type: e.target.value }))}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-primary/50"
                        >
                          <option value="">Select type...</option>
                          {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>

                      {/* Brand + Condition */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[9px] text-muted-foreground/60 mb-0.5 block">Brand</label>
                          <select
                            data-testid="suit-brand-select"
                            value={form.brand}
                            onChange={e => setForm(p => ({ ...p, brand: e.target.value }))}
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-primary/50"
                          >
                            <option value="">Select brand...</option>
                            {brandOptions.map(b => <option key={b} value={b}>{b}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-[9px] text-muted-foreground/60 mb-0.5 block">Condition</label>
                          <select
                            data-testid="suit-condition-select"
                            value={form.condition}
                            onChange={e => setForm(p => ({ ...p, condition: e.target.value }))}
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-primary/50"
                          >
                            {['New', 'Good', 'Fair', 'Thrashed'].map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      </div>

                      {/* Color */}
                      <div>
                        <label className="text-[9px] text-muted-foreground/60 mb-0.5 block">Color (optional)</label>
                        <input
                          data-testid="suit-color-input"
                          value={form.color}
                          onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
                          placeholder="e.g. Black/Blue"
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/30"
                        />
                      </div>

                      {/* Notes */}
                      <div>
                        <label className="text-[9px] text-muted-foreground/60 mb-0.5 block">Notes (optional)</label>
                        <input
                          data-testid="suit-notes-input"
                          value={form.notes}
                          onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                          placeholder="e.g. Winter suit, chest zip"
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/30"
                        />
                      </div>

                      {/* Save */}
                      <button
                        data-testid="suit-save-btn"
                        onClick={handleSave}
                        disabled={!form.suit_type}
                        className="w-full bg-primary text-primary-foreground text-xs font-bold py-2 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center justify-center gap-1.5"
                      >
                        <Check className="w-3.5 h-3.5" />
                        {editingId ? 'Update' : 'Add Suit'}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Suit list */}
              {suits.length > 0 ? (
                <div className="space-y-1.5" data-testid="suits-list">
                  {suits.map((suit, i) => (
                    <motion.div
                      key={suit.suit_id}
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className="group flex items-center gap-2.5 bg-white/[0.02] hover:bg-white/[0.04] border border-white/[0.04] rounded-xl px-3 py-2 transition-colors"
                      data-testid={`suit-item-${suit.suit_id}`}
                    >
                      <span className="text-sm flex-shrink-0">{getThicknessIcon(suit.suit_type)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs font-medium truncate">{suit.suit_type}</p>
                          <span className={`text-[8px] px-1.5 py-0.5 rounded-md ${CONDITION_COLORS[suit.condition] || 'text-muted-foreground bg-white/5'}`}>
                            {suit.condition}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-[9px] text-muted-foreground/40">
                          {suit.brand && <span>{suit.brand}</span>}
                          {suit.brand && suit.color && <span>·</span>}
                          {suit.color && <span>{suit.color}</span>}
                          {suit.notes && <span className="truncate ml-1 italic">— {suit.notes}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <button
                          data-testid={`suit-edit-${suit.suit_id}`}
                          onClick={() => startEdit(suit)}
                          className="p-1 hover:bg-white/10 rounded-md transition-colors"
                        >
                          <Pencil className="w-3 h-3 text-muted-foreground/50" />
                        </button>
                        <button
                          data-testid={`suit-delete-${suit.suit_id}`}
                          onClick={() => handleDelete(suit.suit_id)}
                          className="p-1 hover:bg-red-500/10 rounded-md transition-colors"
                        >
                          <Trash2 className="w-3 h-3 text-red-400/50" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : !showAdd && (
                <div className="text-center py-6">
                  <p className="text-[11px] text-muted-foreground/50">no suits yet</p>
                  <button
                    onClick={() => setShowAdd(true)}
                    className="text-[10px] text-primary hover:underline mt-1"
                  >
                    add your first suit
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
